import * as React from "react";

import "./cc-view.scss";

import {BlockWindowUnload}    from "@Common/react/block-window-unload";
import {cs}                   from "@Common/react/chain-services";
import {consumeContext,
        provideContext}       from "@Common/react/context";
import {fragments}            from "@Common/react/fragments";
import {Invoke}               from "@Common/react/invoke";
import {Load2}                from "@Common/react/load2";
import {QueryString}          from "@Common/react/query-string";
import {TimeoutAction}        from "@Common/react/timeout-action";
import {UseState}             from "@Common/react/use-state";
import {Watch}                from "@Common/react/watch";
import {CcApis3}              from "@Common/tools/cc-integration/cc_apis/cc-apis-3";
import {CcApis3Mock}          from "@Common/tools/cc-integration/cc_apis/mock/cc-apis-3-mock";
import {ResetTemplateBtn}     from "@Common/tools/cc-integration/ui/reset-template-btn/reset-template-btn";
import {AiBanner}             from "@Common/ui-components/ai-banner/ai-banner";
import {isDevXd}              from "@Common/ui-components/debug/is-dev";
import {isDevQuan}            from "@Common/ui-components/debug/is-dev-quan";
import {PhotoDashboard}       from "@Common/ui-components/photo-dashboard/photo-dashboard";
import {RefreshTemplateModal} from "@Common/ui-components/refresh-template-modal/refresh-template-modal";

import {Instruction}  from "../../common/instruction/instruction";
import {InternalInfo} from "../build3/customize-panel/customize-panel-components/internalInfo";
import {SaveAlerts}   from "../common/SaveAlerts";
import {SaveModal}    from "../common/SaveModal";

import {ChangeListingModal} from "./change-listing-modal/change-listing-modal";
import {ExhibitXpresso}     from "./exhibit-xpresso/exhibit-xpresso";
import {SaveButtons}        from "./save-buttons/save-buttons";

export const CcView = (
    {
        item,
        templateOptions,
        renderEmptyLayout,
        listing,
        onDone,
        referrer,
        getItem,
        title,
        listingId,
        isListingsEligible,
        advEditMode,
        orderId,
        componentId,
        next,
    }
) => cs(
    consumeContext("company"),
    consumeContext("apis"),
    consumeContext("userLevels"),
    consumeContext("systemConstants"),
    ["ccHostResponse", ({systemConstants}, next) => Load2({
        fetch: async () => {
            const ccApi = (isDevQuan() ? CcApis3Mock : CcApis3)({host: systemConstants.value.DS_SERVER_URL});
            return await ccApi.getHost({
                templateId: templateOptions.sku.front,
                stateId: loadTemplateFromItem(item, componentId)?.ccStateId
            });
        },
        next
    })],
    ({ccHostResponse}, next) => ccHostResponse.loading ? (
        renderEmptyLayout({
            messages: ["Loading Host..."],
            loading: true,
        })
    ) : !ccHostResponse.value || ccHostResponse.value.ccHost === "" ? ( /* check ccHost for empty string only - if null, then want to continue to next to allow the error modal to display */
        renderEmptyLayout({
            messages: ["Error: Cannot load host."],
            error: true,
        })
    ) : (
        next()
    ),
    ["ccApis", ({ccHostResponse}, next) => next(
        !isDevQuan() ? (
            CcApis3({ host: ccHostResponse.value.ccHost })
        ) : (
            CcApis3Mock({
                host: ccHostResponse.value.ccHost
            })
        )
    )],
    ({ccApis, ccHostResponse}, next) => provideContext({ccApis, ccHostResponse}, next),

    ["userCreatedTemplateData", ({apis}, next) => Load2({
        fetch: () => item.sku.userCreatedTemplate ? apis.next.userCreated.getTemplate(item.sku.userCreatedTemplate) : {},
        next,
    })],
    ({userCreatedTemplateData}, next) => userCreatedTemplateData.loading ? (
        renderEmptyLayout({
            messages: ["Loading template data..."],
            loading: true,
        })
    ) : (
        next()
    ),
    ["templateVariations", ({apis}, next) => Load2({
        fetch: async() => templateOptions.options.find(({devKey}) => devKey === "variation_type")?.value === "master" ? await apis.next.getTemplateVariations(
            !componentId ? item.sku.front : item.components[componentId].sku.front
        ) : null,
        next
    })],
    ({templateVariations}, next) => templateVariations.loading ? (
        renderEmptyLayout({
            messages: ["Loading template data..."],
            loading: true,
        })
    ) : (
        next()
    ),
    ["exhibit", ({templateVariations, userCreatedTemplateData}, next) => (
        isDevQuan() ? (
            MockExhibit({next})
        ) : (
            ExhibitXpresso({
                ccValues: {
                    ...loadTemplateFromItem(item, componentId),
                    userCreatedCcConfig: (userCreatedTemplateData.value?.ccConfig ? JSON.parse(userCreatedTemplateData.value.ccConfig) : "")
                },
                componentId,
                templateIds: !componentId ? (item.sku.back && item.sku.front != item.sku.back ? [item.sku.front, item.sku.back] : [item.sku.front]) : (
                    item.components[componentId].sku.back && item.components[componentId].sku.front != item.components[componentId].sku.back ?
                    [item.components[componentId].sku.front, item.components[componentId].sku.back] : [item.components[componentId].sku.front]
                ),
                inputData: item.metaData?.inputData,
                item,
                listing,
                advEditMode: !!templateOptions?.options?.find((o) => o.devKey === "enable_cc_adv_mode")?.value,
                mockData: null,
                templateVariations: templateVariations.value,
                next,
            })
        )
    )],
    ["surfaceHasAiFields", ({exhibit}, next) => Load2({
        _key: exhibit.initialized && exhibit.currentSideIndex,
        fetch: async() => {
            if (exhibit.initialized) {
                return await exhibit.surfaceHasAiFields();
            } else {
                return false;
            }
        },
        next
    })],
    ["isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)],
    ["submitting", (_, next) => UseState({next})],
    ["buttonClicked", ({}, next) => UseState({next})],

    ["saveConfirmedAlert", (_, next) => UseState({next})],
    ["changeListingModal", ({exhibit}, next) => ChangeListingModal({
        title, listingId, getItem,
        host: exhibit.ccHost,
        stateId: exhibit.ccStateId,
        clearChanged: exhibit.clearChanged,
        orderId,
        next,
    })],
    ({saveConfirmedAlert, exhibit}, next) => SaveModal({
        changeAlertMessage: (msg) => saveConfirmedAlert.onChange(msg),
        onSave: async () => {
            await exhibit.save();
            return true;
        },
        disabled: !exhibit.changed,
        next,
    }),
    ["refreshTemplateModal", ({ccApis, apis, exhibit}, next) => RefreshTemplateModal({
        onRefresh: async () => {
            exhibit.clearChanged();
            await apis.next.gallery.orders.deleteListingImages({orderId});
            let item = await getItem();
            if(item?.metaData?.ccReference){
                item.metaData.oldCcReference = item.metaData.ccReference;
                delete item.metaData.ccReference;
                await apis.next.item.updateItem(item);
            }

            if (isDevXd()) {
                alert("Reloading page after refresh template");
            }

            window.location.reload();
        },
        next,
    })],
    ["saveButtons", ({submitting, exhibit, buttonClicked, saveConfirmedAlert}, next) => next(exhibit.initialized && SaveButtons({
        submitting: submitting,
        buttonClicked: buttonClicked,
        saveConfirmedAlert: saveConfirmedAlert,
        changed: exhibit.changed,
        disableSaves: exhibit.disableSaves,
        isRequired: exhibit.isRequired,
        itemId: item.itemId,
        onDone: async () => onDone(),
        onSave: async () => {
            await exhibit.save();
            return true;
        },
        getMergeFieldErrorMessage: exhibit.getMergeFieldErrorMessageJsx,
        ccStateId: exhibit.ccStateId,
        isComponent: !!componentId,
        distributionType: item?.selections?.distributionType,
        isApprovalAfterProof: (item.approvalInfo?.location === 'proof' && referrer !== 'approval-requests'),
    }))],
    ["resetTemplateBtn", ({refreshTemplateModal}, next) => next(ResetTemplateBtn({
        refreshTemplateModal: refreshTemplateModal,
    }))],
    ["importModal", ({}, next) => PhotoDashboard({next})],
    ({exhibit}, next) => fragments(
        next(),

        syncSideIndex({exhibit}),
    ),

    ({exhibit, saveConfirmedAlert, changeListingModal, refreshTemplateModal, saveButtons, isInternalUser, resetTemplateBtn, importModal, company, surfaceHasAiFields}) => next({
        beforeNav: () => exhibit.showWarning(),
        render: () => (
            // ( submitting.value ? (document.body.style.cursor="wait") : (document.body.style.cursor="auto") ),
            <div className="cc-view-82j" style={{position: "relative"}}>
                {exhibit.changed && BlockWindowUnload({})}

                {!exhibit.initialized && (
                    <div className="container">
                        <div className="overlay" />
                        <img className="loadingImage" {...{
                            src: exhibit.proofImages?.[0] ?? "assets/SPINNER.svg",
                        }}/>

                        {exhibit.proofImages?.[0] && !advEditMode ? (
                            <button
                                className="retry"
                                onClick={()=>window.location.reload()}
                                xd-qa-type="button"
                                xd-qa-name="Retry"
                            >Retry</button>
                        ) : exhibit.proofImages?.[0] == "assets/SPINNER_ERROR_STATE_FAILED.svg" && (
                            <button
                                className="retry"
                                onClick={()=>refreshTemplateModal.show()}
                                xd-qa-type="button"
                                xd-qa-name="Refresh Template"
                            >
                                Refresh Template
                            </button>
                        )}
                    </div>
                )}

                {saveConfirmedAlert.value && exhibit.ccHost && <>
                    {SaveAlerts({
                        message: saveConfirmedAlert.value,
                        onClose: () => saveConfirmedAlert.onChange(null),
                    })}

                    {saveConfirmedAlert.value.success && TimeoutAction({
                        delay: 4000,
                        action: () => saveConfirmedAlert.onChange(null),
                    })}
                </>}

                <div className="main">
                    <div className="controls">
                        {exhibit.controls}
                    </div>
                    {advEditMode && exhibit.editForm}

                    <div className="line2">

                        <div className="left">
                            <div className="editor" style={ advEditMode ? {marginRight: "0px"} : {} }>
                                <div className={(exhibit.fullScreen) ? "editor-items-fullscreen" : "editor-items-standard"}>
                                    <div className="content">
                                        {exhibit.render()}
                                    </div>
                                    <div className="download-reset-buttons">
                                        {exhibit.initialized && (<>
                                            <div>
                                                {exhibit.watermarkDownload}
                                                {templateOptions?.["allowSocialShareMain"] && exhibit.socialDownloadBtn}
                                                {isInternalUser && exhibit.prodFileDownload}
                                            </div>
                                            <div>
                                                {advEditMode && saveButtons}
                                                {isInternalUser && resetTemplateBtn}
                                            </div>
                                        </>)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!advEditMode && (
                            <div className="details">
                                {( exhibit.initialized && company.prefs.suggestedCommentsMode > 0 && surfaceHasAiFields.value ) ? <AiBanner isCC={true}/> : null}
                                {exhibit.initialized && <>
                                    <div className="row">
                                        {isListingsEligible && !componentId && (//Don't want kit components to change order listing data.
                                            <div className="listing">
                                                <div className="box" onClick={changeListingModal.show}>
                                                    <div className="change-listing">
                                                        <div className="text">
                                                            Change Listing
                                                        </div>
                                                        <i className="fa fa-caret-down"/>
                                                    </div>
                                                    <div className="title">
                                                        {title}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {!company.prefs?.customersCanvasUsesDefaultAssetManager
                                        && !!templateOptions?.options?.find((o) => o.devKey === "allow_property_photo_previous_order")?.value
                                        && (
                                            <div className="image-import">
                                                <button
                                                    xd-qa-name="ccmodal previous order popup button"
                                                    xd-qa-type="button"
                                                    onClick={() => importModal.show({
                                                        orderId: +orderId,
                                                        onLoadSurfaceData: exhibit.getProductSurfacesWithImages,
                                                        onApplyImport: exhibit.importImages,
                                                    })}
                                                >
                                                    Import Photos from a Previous Order
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {Instruction({
                                        text: "Click on the template to make edits directly",
                                    })}

                                    {exhibit.editForm}
                                    {isInternalUser && <InternalInfo item={item} isBuild3Page={false} ccVersion={exhibit.ccVersion} />}
                                    <div className="controls">
                                        <div className="buttons">
                                            {saveButtons}
                                        </div>
                                    </div>

                                </>}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        ),
    }),
);

const MockExhibit = ({next}) => next({
    ccStateId: 123,
    initialized: true,
    render: () => "Mock exhibit",
    controls: "Mock controls",
    editForm: "Mock editForm",
    save: () => {},
    currentSideIndex: 0,
    showWarning: async () => true,
    checkIsRequired: async () => false,
    highResProducts: {jpg:[]},
});

const syncSideIndex = ({exhibit}) => cs(
    ["side", ({}, next) => QueryString({
        param: "side",
        next: ({value, onChange}) => next({value: value == null ? 0 : +value, onChange}),
    })],
    ({side}) => exhibit.currentSideIndex != null && <>

        {Invoke({
            fn: () => {
                if (exhibit.currentSideIndex != side.value) {
                    exhibit.changeSide(+side.value);
                }
            },
        })}

        {Watch({
            value: exhibit.currentSideIndex,
            onChanged: side.onChange,
        })}

    </>,
);


const loadTemplateFromItem = (item, componentId) => (
    componentId ? {
        ccHost          : item?.components?.[componentId]?.metaData?.ccHost,
        ccStateId       : item?.components?.[componentId]?.metaData?.ccReference,
        ccVersion       : item?.components?.[componentId]?.metaData?.ccVersion,
        highResProducts : item?.components?.[componentId]?.metaData?.ccReferenceURLs,
        inputData       : item?.components?.[componentId]?.metaData?.inputData,
        proofImages     : item?.components?.[componentId]?.metaData?.ccProofUrls,
    } : {
        ccHost          : item.metaData?.ccHost,
        ccStateId       : item.metaData?.ccReference,
        ccVersion       : item.metaData?.ccVersion,
        highResProducts : item.metaData?.ccReferenceURLs,
        inputData       : item.metaData?.inputData,
        proofImages     : item.metaData?.ccProofUrls,
    }
);